import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";
import { useRoleContext } from "hooks";
import {
  buildCarrierVerificationMailtoLink,
  CARRIER_VERIFICATION_TOOLTIP,
} from "utils";

import type { CompanyProfile } from "types/company";

// Stable `field` key for the verification requirement, mirroring
// CARRIER_VERIFICATION_FIELD in
// application/company/services/company_profile/company_profile_service.py.
// Unlike the other missing fields, verification is not editable from the
// profile editor, so its line links carriers to the same email-based
// verification flow as
// frontend/src/components/Banner/components/UnverifiedCarrierBanner/UnverifiedCarrierBanner.tsx
// instead of relying on the "Update profile" CTA.
const CARRIER_VERIFICATION_FIELD = "verified";

interface Props {
  companyProfile: CompanyProfile;
  onEditProfile: VoidFunction;
}

export function CarrierProfileCompletionAlert(props: Props) {
  const { companyProfile, onEditProfile } = props;
  const { company, hasCompanyAdminRole } = useRoleContext();
  const posthog = usePostHog();

  const isOwnCompany = company?.id === companyProfile.company.id;
  const { profile_completion: profileCompletion, admin_contact: adminContact } =
    companyProfile;

  // External carriers are suppressed from the nav Banner, but they still get the
  // profile-page completion alert: the profile page is where they go to improve
  // discoverability, so the nudge belongs here even though the banner is hidden.
  if (!isOwnCompany || profileCompletion.is_complete) {
    return null;
  }

  const isAdmin = hasCompanyAdminRole();

  const onCompleteProfileClick = () => {
    posthog?.capture(
      ClientAppEvent.CARRIER_PROFILE_COMPLETION_ALERT_CTA_CLICKED,
    );
    onEditProfile();
  };

  const onAdminContactClick = () => {
    posthog?.capture(
      ClientAppEvent.CARRIER_PROFILE_COMPLETION_ALERT_ADMIN_CONTACT_CLICKED,
    );
  };

  return (
    <Box sx={{ mb: "1.5rem" }}>
      <Alert
        severity="warning"
        action={
          isAdmin ? (
            <Tooltip title="Open the profile editor to add the missing information.">
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={onCompleteProfileClick}
              >
                Update profile
              </Button>
            </Tooltip>
          ) : undefined
        }
      >
        <AlertTitle>Update your carrier profile</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Add the following so shippers can find you:
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {profileCompletion.missing_fields.map((missingField) =>
            missingField.field === CARRIER_VERIFICATION_FIELD ? (
              <Tooltip
                key={missingField.field}
                title={CARRIER_VERIFICATION_TOOLTIP}
              >
                <Chip
                  label={missingField.label}
                  size="small"
                  color="warning"
                  variant="outlined"
                  clickable
                  component="a"
                  href={buildCarrierVerificationMailtoLink(
                    companyProfile.company.name,
                  )}
                />
              </Tooltip>
            ) : (
              <Chip
                key={missingField.field}
                label={missingField.label}
                size="small"
                color="warning"
                variant="outlined"
              />
            ),
          )}
        </Stack>
        {!isAdmin && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Only a company administrator can update this profile.{" "}
            {adminContact ? (
              <>
                Contact {adminContact.first_name} {adminContact.last_name} at{" "}
                <Tooltip title="Email your company administrator to request these updates.">
                  <Link
                    href={`mailto:${adminContact.email}`}
                    onClick={onAdminContactClick}
                  >
                    {adminContact.email}
                  </Link>
                </Tooltip>
                .
              </>
            ) : (
              "Ask an administrator on your team to add the missing information."
            )}
          </Typography>
        )}
      </Alert>
    </Box>
  );
}
